import axios from 'axios';

const recoveryClient = axios.create({
  baseURL: (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, ''),
  timeout: 20000,
});

const positiveSeconds = (value, fallback) => Number.isFinite(Number(value)) && Number(value) > 0
  ? Math.min(Math.ceil(Number(value)), 3600) : fallback;

async function postRecovery(path, payload) {
  let response;
  try {
    response = await recoveryClient.post(path, payload);
  } catch (error) {
    const serverMessage = error.response?.data?.error;
    const message = error.response?.status === 404 ? '계정 찾기 서비스를 준비 중입니다. 잠시 후 다시 이용해 주세요.'
      : typeof serverMessage === 'string' ? serverMessage
      : error.code === 'ECONNABORTED' ? '응답 시간이 지났습니다. 잠시 후 다시 확인해 주세요.'
        : error.response ? '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.'
          : '서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.';
    const failure = new Error(message);
    failure.status = error.response?.status;
    if (failure.status === 429) failure.retryAfter = positiveSeconds(error.response?.data?.retryAfter, 60);
    throw failure;
  }
  const data = response?.data;
  if (data?.success !== true) throw new Error('응답을 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.');
  return data;
}

export async function requestAccountRecovery({ purpose, name, phone, id }) {
  const payload = { purpose, name: name.trim(), phone: phone.replace(/[\s-]/g, '') };
  if (purpose === 'password') payload.id = id.trim();
  const data = await postRecovery('/account-recovery/request', payload);
  if (typeof data.challengeId !== 'string' || !data.challengeId) throw new Error('인증 요청을 확인하지 못했습니다. 다시 시도해 주세요.');
  return {
    challengeId: data.challengeId,
    expiresIn: positiveSeconds(data.expiresIn, 300),
    retryAfter: positiveSeconds(data.retryAfter, 60),
  };
}

export async function verifyAccountRecovery({ challengeId, code }, purpose) {
  const data = await postRecovery('/account-recovery/verify', { challengeId, code });
  if (purpose === 'password') {
    if (typeof data.resetToken !== 'string' || !data.resetToken) throw new Error('인증 결과를 확인하지 못했습니다. 인증번호를 다시 요청해 주세요.');
    return { resetToken: data.resetToken, expiresIn: positiveSeconds(data.expiresIn, 300) };
  }
  if (!Array.isArray(data.accounts) || !data.accounts.length || data.accounts.some((account) => typeof account?.id !== 'string' || !account.id)) {
    throw new Error('아이디 확인을 완료하지 못했습니다. 입력한 정보를 다시 확인해 주세요.');
  }
  return { accounts: data.accounts.map(({ id }) => ({ id })) };
}

export async function resetAccountPassword({ resetToken, password }) {
  await postRecovery('/account-recovery/reset', { resetToken, password });
}

const hasControlCharacters = (value) => Array.from(value).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127);

export function validateRecoveryIdentity({ purpose, name, phone, id }) {
  if (!name.trim() || name.trim().length > 20 || hasControlCharacters(name)) return '가입한 이름을 20자 이내로 입력해 주세요.';
  if (!/^01[0-9]{8,9}$/.test(phone.replace(/[\s-]/g, ''))) return '가입한 휴대전화 번호를 정확히 입력해 주세요.';
  if (purpose === 'password' && (!id.trim() || id.trim().length > 30 || /\s/.test(id.trim()) || hasControlCharacters(id))) return '가입한 아이디를 입력해 주세요.';
  return '';
}

export function validateRecoveryPassword(password, confirmation) {
  if (password.length < 6 || hasControlCharacters(password)) return '새 비밀번호는 6자 이상 입력해 주세요.';
  try {
    if (encodeURIComponent(password).replace(/%[0-9A-F]{2}/g, '_').length > 31) return '비밀번호가 너무 깁니다. 영문·숫자는 최대 31자이며, 한글·특수문자는 더 짧게 입력해 주세요.';
  } catch { return '사용할 수 없는 문자가 포함되어 있습니다.'; }
  if (password !== confirmation) return '새 비밀번호가 일치하지 않습니다.';
  return '';
}
